import { SvgIconComponent } from "@mui/icons-material";
import {
  Avatar,
  Box,
  Chip,
  Divider,
  Fade,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Popper,
  Stack,
  SvgIcon,
  Typography,
} from "@mui/material";
import icAvatar from "assets/icons/avatar.png";
import logo from "assets/images/logo.png";
import { ULink } from "components/utils/Link";
import { ColorVariables, SandboxUrl } from "configs";
import dayjs from "dayjs";
import { mapModifiers, rem } from "utils";

import { useHeader } from "./hooks";
import { StyledBadge } from "./styles";

export interface ProfileMenuItem {
  id: string;
  label: string;
  path?: string;
  icon: SvgIconComponent;
  onClick?: () => void;
}

export interface Props {
  accountName: string;
  accountRole?: string;
  open: boolean;
  profileMenu: Array<ProfileMenuItem>;
  onToggle?: () => void;
}

export const Header: React.FC<Props> = ({
  accountName,
  accountRole,
  open,
  profileMenu,
  onToggle,
}) => {
  const {
    anchorProfileEl,
    handleClickProfileMenuItem,
    handleToggleProfileMenu,
  } = useHeader();

  const hour = dayjs().hour();

  return (
    <div className="O-Header">
      <div className="O-Header_Left">
        <ULink to={SandboxUrl as string}>
          <img className="O-Header_Logo" src={logo} alt="Talentnet Logo" />
        </ULink>
        <div
          className={mapModifiers("O-Header_HamburgerButton", open && "Open")}
          onClick={onToggle}
          role="button"
          aria-hidden="true"
        >
          <div className="O-Header_HamburgerIcon" />
        </div>
      </div>
      <div className="O-Header_Right">
        <Chip
          clickable
          variant="outlined"
          label={
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="O-Header_SettingIcon"
              width={24}
              height={24}
              viewBox="0 0 24 24"
              strokeWidth="1.5"
              stroke={ColorVariables.DodgerBlue}
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path stroke="none" d="M0 0h24v24H0z" fill="none" />
              <path d="M10.325 4.317c.426 -1.756 2.924 -1.756 3.35 0a1.724 1.724 0 0 0 2.573 1.066c1.543 -.94 3.31 .826 2.37 2.37a1.724 1.724 0 0 0 1.065 2.572c1.756 .426 1.756 2.924 0 3.35a1.724 1.724 0 0 0 -1.066 2.573c.94 1.543 -.826 3.31 -2.37 2.37a1.724 1.724 0 0 0 -2.572 1.065c-.426 1.756 -2.924 1.756 -3.35 0a1.724 1.724 0 0 0 -2.573 -1.066c-1.543 .94 -3.31 -.826 -2.37 -2.37a1.724 1.724 0 0 0 -1.065 -2.572c-1.756 -.426 -1.756 -2.924 0 -3.35a1.724 1.724 0 0 0 1.066 -2.573c-.94 -1.543 .826 -3.31 2.37 -2.37c1 .608 2.296 .07 2.572 -1.065z" />
              <circle cx="12" cy="12" r="3" />
            </svg>
          }
          avatar={
            <StyledBadge
              overlap="circular"
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              variant="dot"
            >
              <Avatar alt="Avatar" src={icAvatar} />
            </StyledBadge>
          }
          onClick={handleToggleProfileMenu}
        />
      </div>
      <Popper
        open={!!anchorProfileEl}
        anchorEl={anchorProfileEl}
        transition
        className="O-Header_ProfileMenu"
        placement="bottom-start"
        disablePortal
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={350}>
            <Paper elevation={4}>
              <Box padding={rem(16)}>
                <Stack flexDirection="row">
                  <Typography
                    fontWeight={500}
                    variant="subtitle1"
                    lineHeight={1}
                  >
                    Good&nbsp;
                    {/* eslint-disable-next-line no-nested-ternary */}
                    {hour < 12
                      ? "Morning"
                      : hour < 18
                      ? "Afternoon"
                      : "Evening"}
                    ,&nbsp;
                  </Typography>
                  <Typography
                    fontWeight={700}
                    variant="subtitle1"
                    color={ColorVariables.MediumCarmine}
                    lineHeight={1}
                  >
                    {accountName}
                  </Typography>
                </Stack>
                {accountRole && (
                  <Typography variant="caption" lineHeight={1}>
                    {accountRole}
                  </Typography>
                )}
              </Box>
              <Divider />
              <List>
                {profileMenu.map(({ id, label, path, icon, onClick }) => (
                  <ListItemButton
                    key={id}
                    onClick={() =>
                      handleClickProfileMenuItem({ path, onClick })
                    }
                  >
                    <ListItemIcon>
                      <SvgIcon component={icon} />
                    </ListItemIcon>
                    <ListItemText primary={label} />
                  </ListItemButton>
                ))}
              </List>
            </Paper>
          </Fade>
        )}
      </Popper>
    </div>
  );
};
